import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { errorText } from "../../i18n/serverErrors";
import type { InventoryBalance, Zone } from "../../api/types";
import {
  FLOOR_ITEM_THUMB_SOURCE,
  shopModeApi,
  type FloorCatalogueItem,
} from "../../api/shopMode";
import { ItemThumb } from "../ItemThumb";
import { ShopFloorIcon } from "./ShopFloorIcon";
import { ShopFloorBarcodeResolver } from "./ShopFloorBarcodeResolver";
import { ShopFloorPhotoSearch } from "./ShopFloorPhotoSearch";
import { ShopFloorItemCard } from "./ShopFloorItemCard";
import { ShopFloorBoxCard } from "./ShopFloorBoxCard";
import { matchesInventorySearch } from "../../utils/inventorySearch";
import { useT } from "../../i18n/LanguageProvider";
import { categoryLabel } from "../../i18n/taxonomy";
import { t as translate } from "../../i18n/t";
import { placeWords, type StockPlace } from "../../utils/placeWords";
import { compareText, money } from "../../i18n/intl";

/**
 * G-75 — "where is it, and what does it cost?", answered in one screen.
 *
 * The flow this replaces cost a florist four taps with a customer standing
 * there: fob → See stock → pick a zone → Scan → scan. The zone tap was the
 * tell — a zone is part of the ANSWER ("it's in the Fridge"), never a
 * precondition of the question. So this searches the whole shop at once and
 * puts the zone in the result row where it belongs.
 *
 * Landing here instead of on Bouquets is the owner's ruling (2026-09-15):
 * a florist opening "See stock" is nearly always looking for one thing.
 *
 * The price shown is what the CUSTOMER pays. That needs saying because the
 * column this screen replaces showed purchase COST under a heading reading
 * "Price" — the brown Trespolo vase listed at €13.20 against a real €31.94.
 */

/** One shop-wide result: an item, and everywhere in this shop it is sitting. */
type FoundItem = {
  itemId: string;
  name: string;
  imageKey: string | null;
  saleUnitPriceEur: string | null;
  saleUnitPriceIsDerived: boolean;
  totalQty: number;
  places: StockPlace[];
};

function groupByItem(rows: InventoryBalance[]): FoundItem[] {
  const byItem = new Map<string, FoundItem>();
  for (const row of rows) {
    const qty = parseFloat(row.qtyOnHand);
    const existing = byItem.get(row.item.id);
    const place: StockPlace = {
      zoneName: row.zone.name,
      qty: row.qtyOnHand,
      box: row.box ?? null,
    };
    if (existing) {
      existing.places.push(place);
      existing.totalQty += Number.isFinite(qty) ? qty : 0;
      continue;
    }
    byItem.set(row.item.id, {
      itemId: row.item.id,
      name: row.item.name,
      imageKey: row.item.imageKey ?? null,
      saleUnitPriceEur: row.saleUnitPriceEur ?? null,
      saleUnitPriceIsDerived: row.saleUnitPriceIsDerived ?? false,
      totalQty: Number.isFinite(qty) ? qty : 0,
      places: [place],
    });
  }
  return [...byItem.values()].sort((a, b) => compareText(a.name, b.name));
}

/** How long typing must settle before a search counts as a look-up worth logging. */
const LOOKUP_SETTLE_MS = 900;
/** The owner wants to know who saw a price; a 200-row list answers that for nothing. */
const LOOKUP_MAX_IDS = 10;

/**
 * G-89 — an item the shop does not hold is still an answer.
 *
 * Until now this screen could only show what was already on a shelf, so an
 * item that exists in the catalogue but has never been received here simply
 * was not findable: the florist had to scan it, and a missing or damaged
 * barcode left no route at all. Now typing brings those up too, under the
 * stock, marked "0 in stock" — tapping one opens the same card, which offers
 * Receive (G-86).
 *
 * Capped and gated on two typed characters at the owner's request: a single
 * letter matches most of a 600-item catalogue and would bury the stock the
 * florist is actually standing next to.
 */
const CATALOGUE_MIN_CHARS = 2;
const CATALOGUE_MAX_RESULTS = 12;
/** Typing settles this long before the catalogue is asked — one read, not one per key. */
const CATALOGUE_SETTLE_MS = 350;

export function ShopFloorItemFinder({
  shelfZones = [],
}: {
  /** G-84: the shop's shelf zones, for the new-box and box-card forms. */
  shelfZones?: Zone[];
}) {
  const { t } = useT();
  const [rows, setRows] = useState<InventoryBalance[]>([]);
  const [openBoxId, setOpenBoxId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  /** Owner request 2026-09-15: narrow by category beside the search field. "" = every category. */
  const [categoryId, setCategoryId] = useState("");
  const [scanning, setScanning] = useState(false);
  /**
   * G-80: the Photo button exists only when the server says photo search is
   * on, keyed and under budget — never a dead button (G-27's rule). Checked
   * once per visit; a 503 mid-search is handled by the search screen itself.
   */
  const [photoAvailable, setPhotoAvailable] = useState(false);
  const [photoSearching, setPhotoSearching] = useState(false);
  const [openItemId, setOpenItemId] = useState<string | null>(null);
  /** G-89: catalogue matches, whether or not this shop holds any. */
  const [catalogue, setCatalogue] = useState<FloorCatalogueItem[]>([]);
  const [scanNote, setScanNote] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // No zoneId: the whole shop, every zone, in one read. The endpoint has
      // always supported this — only the UI insisted on a zone first.
      setRows(await shopModeApi.floorBalance({}));
    } catch (err: unknown) {
      setError(errorText(err, "itemFinder.couldNotLoad"));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    void shopModeApi
      .floorPhotoSearchStatus()
      .then((status) => {
        if (!cancelled) setPhotoAvailable(status.available);
      })
      .catch(() => {
        if (!cancelled) setPhotoAvailable(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /** The categories actually on the shelves of this shop, for the dropdown. */
  const categories = useMemo(() => {
    const byId = new Map<string, { id: string; code: string; label: string }>();
    for (const row of rows) {
      byId.set(row.item.category.id, {
        id: row.item.category.id,
        code: row.item.category.code,
        label: row.item.category.label,
      });
    }
    return [...byId.values()].sort((a, b) =>
      compareText(categoryLabel(a.code, a.label), categoryLabel(b.code, b.label)),
    );
  }, [rows]);

  /**
   * Owner ruling 2026-09-15 (evening): the whole shop is on screen from the
   * first moment, and typing or picking a category narrows it. The per-zone
   * tabs went with that — every row already says where the item sits.
   */
  const found = useMemo(() => {
    const typed = query.trim();
    return groupByItem(
      rows.filter(
        (r) =>
          (!categoryId || r.item.category.id === categoryId) &&
          (!typed || matchesInventorySearch(r, typed)),
      ),
    );
  }, [rows, query, categoryId]);

  /**
   * Log the search once the typing settles — never per keystroke. The owner
   * asked to know who checks prices; that question is about a look-up, not
   * about every letter on the way to one.
   *
   * The signature is what the effect watches, and the last logged signature is
   * remembered, because a re-render that changes `found`'s identity without
   * changing its CONTENT would otherwise write the row again: the first live
   * test on the tablet produced two identical rows for one search of "rose".
   * At the volume the owner accepted, a silent doubling is the difference
   * between a usable log and an unreadable one.
   */
  const signature = useMemo(
    () =>
      `${query.trim()}|${found
        .slice(0, LOOKUP_MAX_IDS)
        .map((f) => f.itemId)
        .join(",")}`,
    [query, found],
  );
  const lastLoggedRef = useRef<string | null>(null);

  useEffect(() => {
    const typed = query.trim();
    if (typed.length < 2) return;
    if (lastLoggedRef.current === signature) return;
    const timer = window.setTimeout(() => {
      lastLoggedRef.current = signature;
      void shopModeApi.floorRecordLookup({
        how: "SEARCH",
        query: typed,
        itemIds: found.slice(0, LOOKUP_MAX_IDS).map((f) => f.itemId),
      });
    }, LOOKUP_SETTLE_MS);
    return () => window.clearTimeout(timer);
  }, [signature, query, found]);

  /**
   * Ask the catalogue once the typing settles. A failure is silent on purpose:
   * the stock list above is the answer the florist came for, and an alert over
   * a secondary lookup would read as if the search itself had broken.
   */
  useEffect(() => {
    const typed = query.trim();
    if (typed.length < CATALOGUE_MIN_CHARS) {
      setCatalogue([]);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      void shopModeApi
        .floorSearchCatalogue(typed, CATALOGUE_MAX_RESULTS)
        .then((items) => {
          if (!cancelled) setCatalogue(items);
        })
        .catch(() => {
          if (!cancelled) setCatalogue([]);
        });
    }, CATALOGUE_SETTLE_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [query]);

  /**
   * What the catalogue knows and the shelves do not. The category filter is
   * honoured by label, because that is all the floor route returns.
   *
   * Judged against EVERY balance row in the shop, not against `found`. The
   * two searches do not match on the same fields — the stock list's matcher
   * reads name, shop, zone and barcode, while the catalogue also matches SKU
   * — so subtracting the filtered list would put an item that is on a shelf
   * under "not in this shop yet, 0 in stock" whenever it was found by SKU,
   * and the florist would receive a second lot of stock they already have.
   */
  const notStockedHere = useMemo(() => {
    const stocked = new Set(rows.map((r) => r.item.id));
    const label = categories.find((c) => c.id === categoryId)?.label ?? null;
    return catalogue.filter(
      (c) => !stocked.has(c.id) && (!label || c.categoryLabel === label),
    );
  }, [catalogue, rows, categories, categoryId]);

  const priceWords = (item: FoundItem) => {
    if (!item.saleUnitPriceEur) return t("itemFinder.priceUnknown");
    return money(item.saleUnitPriceEur);
  };

  return (
    <div className="shop-floor-stock-flow sf-finder">
      <div className="sf-finder-bar">
        <label htmlFor="sf-finder-search" className="visually-hidden">
          {t("itemFinder.searchLabel")}
        </label>
        <div className="inventory-search-field sf-finder-field">
          <ShopFloorIcon name="search" size={22} />
          {/*
            Deliberately NOT autofocused. Focusing on arrival opens the soft
            keyboard, which on the shop's Tab Active5 covers a little over half
            the screen — measured on the device: one result row visible instead
            of five, with Scan sitting behind a keyboard the florist has to
            dismiss first. Typing costs one tap on the field; arriving costs
            nothing.
          */}
          <input
            id="sf-finder-search"
            type="search"
            className="inventory-search-input"
            placeholder={t("itemFinder.placeholder")}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setScanNote(null);
            }}
            autoComplete="off"
          />
        </div>
        <label htmlFor="sf-finder-category" className="visually-hidden">
          {t("itemFinder.narrowByCategory")}
        </label>
        <select
          id="sf-finder-category"
          className="inventory-category-select sf-finder-category"
          value={categoryId}
          onChange={(e) => {
            setCategoryId(e.target.value);
            setScanNote(null);
          }}
        >
          <option value="">{t("itemFinder.allCategories")}</option>
          {categories.map((cat) => (
            <option key={cat.id} value={cat.id}>
              {categoryLabel(cat.code, cat.label)}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="button sf-finder-scan-btn"
          onClick={() => {
            setScanNote(null);
            setScanning(true);
          }}
        >
          {t("itemFinder.scan")}
        </button>
        {/* G-84: "Create a new box" used to sit here beside Scan (OD-50). The
            owner removed it on 2026-09-17 — this row is for finding an item,
            and issuing a box is not that. It lives on the Boxes tab alone. */}
        {photoAvailable ? (
          <button
            type="button"
            className="button sf-finder-scan-btn sf-finder-photo-btn"
            onClick={() => {
              setScanNote(null);
              setPhotoSearching(true);
            }}
          >
            <ShopFloorIcon name="camera" size={22} />
            {t("itemFinder.photo")}
          </button>
        ) : null}
      </div>

      {error ? <div className="alert error">{error}</div> : null}
      {scanNote ? <div className="alert info sf-finder-note">{scanNote}</div> : null}

      {loading ? (
        <div className="empty-state">{t("itemFinder.loadingShop")}</div>
      ) : found.length === 0 && notStockedHere.length === 0 ? (
        <div className="empty-state">
          {query.trim()
            ? categoryId
              ? t("itemFinder.noMatchInCategory", { query: query.trim() })
              : t("itemFinder.noMatch", { query: query.trim() })
            : categoryId
              ? t("itemFinder.noneInCategory")
              : t("itemFinder.nothingInStock")}
        </div>
      ) : found.length === 0 ? null : (
        <ul className="sf-finder-results">
          {found.map((item) => (
            <li key={item.itemId}>
              <button
                type="button"
                className="sf-finder-result"
                onClick={() => setOpenItemId(item.itemId)}
              >
                <ItemThumb
                  itemId={item.itemId}
                  imageKey={item.imageKey}
                  alt={item.name}
                  imageSource={FLOOR_ITEM_THUMB_SOURCE}
                />
                <span className="sf-finder-result-main">
                  <span className="sf-finder-result-name">{item.name}</span>
                  <span className="sf-finder-result-where">
                    {item.places.map((p, i) => (
                      <span key={`${p.zoneName}-${i}`}>
                        {i > 0 ? " · " : null}
                        <span className={p.box ? "sf-finder-in-box" : undefined}>
                          {placeWords(p)}
                        </span>
                      </span>
                    ))}
                  </span>
                </span>
                <span className="sf-finder-result-price">
                  {priceWords(item)}
                  {item.saleUnitPriceEur && item.saleUnitPriceIsDerived ? (
                    <small className="muted">{t("itemFinder.workedOut")}</small>
                  ) : null}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {!loading && notStockedHere.length > 0 ? (
        <div className="sf-finder-group">
          <p className="sf-finder-group-label">{t("itemFinder.notInShopYet")}</p>
          <ul className="sf-finder-results">
            {notStockedHere.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  className="sf-finder-result sf-finder-result--zero"
                  onClick={() => setOpenItemId(item.id)}
                >
                  <ItemThumb
                    itemId={item.id}
                    imageKey={item.imageKey}
                    alt={item.name}
                    imageSource={FLOOR_ITEM_THUMB_SOURCE}
                  />
                  <span className="sf-finder-result-main">
                    <span className="sf-finder-result-name">{item.name}</span>
                    <span className="sf-finder-result-where">
                      {t("itemFinder.zeroInStock")} ·{" "}
                      {categoryLabel(item.categoryCode, item.categoryLabel)}
                    </span>
                  </span>
                  <span className="sf-finder-result-price">
                    {item.saleUnitPriceEur
                      ? money(item.saleUnitPriceEur)
                      : t("itemFinder.priceUnknown")}
                    {item.saleUnitPriceEur && item.saleUnitPriceIsDerived ? (
                      <small className="muted">{t("itemFinder.workedOut")}</small>
                    ) : null}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {scanning ? (
        <ShopFloorBarcodeResolver
          onCancel={() => setScanning(false)}
          onBoxResolved={(box) => {
            // G-84: a box label opens the box, not an item.
            setScanning(false);
            setOpenBoxId(box.id);
          }}
          onResolved={(scanned) => {
            setScanning(false);
            void shopModeApi.floorRecordLookup({
              how: "SCAN",
              itemIds: [scanned.id],
            });
            // G-61: a barcode can name several items and the resolver has
            // already asked which one. By here it is settled.
            const stocked = rows.some((r) => r.item.id === scanned.id);
            setScanNote(
              stocked
                ? null
                : t("itemFinder.notInStockAnywhere", { name: scanned.name }),
            );
            setOpenItemId(scanned.id);
          }}
        />
      ) : null}

      {photoSearching ? (
        <ShopFloorPhotoSearch
          onCancel={() => setPhotoSearching(false)}
          onDismissed={() => {
            setPhotoSearching(false);
            setScanNote(translate("itemFinder.notFoundByPhoto"));
          }}
          onPicked={(picked) => {
            // The server logged this pick as the look-up (ITEM_LOOKED_UP,
            // how: PHOTO); calling floorRecordLookup here would double it.
            setPhotoSearching(false);
            setOpenItemId(picked.id);
          }}
        />
      ) : null}

      {openItemId ? (
        <ShopFloorItemCard
          itemId={openItemId}
          onClose={() => setOpenItemId(null)}
          onChanged={() => void load()}
        />
      ) : null}

      {openBoxId ? (
        <ShopFloorBoxCard
          boxId={openBoxId}
          shelfZones={shelfZones}
          onClose={() => setOpenBoxId(null)}
          onChanged={() => void load()}
          onOpenItem={(itemId) => setOpenItemId(itemId)}
        />
      ) : null}
    </div>
  );
}
